// Position Policy evaluators. Compute an anchor point + offset that Movement
// will then steer toward. See docs/04 §4.2.1 (Position Policy 7 rules) and
// docs/05-combat-sim §5.6.
import { distance, direction, scale } from './types.js';
const TANK_OFFSET = 4; // metres behind the tank
const FLANK_RADIUS = 2;
const SAFE_LINE_BACK = 6;
const TRIANGLE_RADIUS = 3;
export function computeAnchor(self, state, rule, targetId) {
    const allies = state.entities.filter((e) => e.side === self.side && e.hp > 0 && e.id !== self.id);
    const target = targetId !== null ? state.entities.find((e) => e.id === targetId) : undefined;
    switch (rule) {
        case 'pos_behind_tank_anchor': {
            // Anchor = strongest ally (highest hpMax) + offset toward me, opposite of target.
            const anchor = pickAlly(allies, (a) => a.hpMax) ?? self;
            if (!target)
                return anchor.pos;
            const away = direction(target.pos, anchor.pos); // from target to anchor
            return {
                x: anchor.pos.x + away.x * TANK_OFFSET,
                y: anchor.pos.y + away.y * TANK_OFFSET,
            };
        }
        case 'pos_behind_target': {
            if (!target)
                return self.pos;
            const away = direction(self.pos, target.pos); // self → target
            // Stand on the far side of target.
            return {
                x: target.pos.x + away.x * TANK_OFFSET,
                y: target.pos.y + away.y * TANK_OFFSET,
            };
        }
        case 'pos_flank_target_left':
            return flankPoint(self, target, +1);
        case 'pos_flank_target_right':
            return flankPoint(self, target, -1);
        case 'pos_ally_triangle': {
            // Try to stand TRIANGLE_RADIUS from the two nearest allies.
            const sorted = allies
                .slice()
                .sort((a, b) => distance(self.pos, a.pos) - distance(self.pos, b.pos));
            if (sorted.length < 2)
                return self.pos;
            const [a, b] = sorted;
            // Bisector direction from a → b's normal at the midpoint.
            const mx = (a.pos.x + b.pos.x) / 2;
            const my = (a.pos.y + b.pos.y) / 2;
            const tangent = direction(a.pos, b.pos);
            const normal = { x: -tangent.y, y: tangent.x };
            // Pick the side of the line closer to current self.
            const towardSelf = normal.x * (self.pos.x - mx) + normal.y * (self.pos.y - my) >= 0 ? 1 : -1;
            return {
                x: mx + normal.x * TRIANGLE_RADIUS * towardSelf,
                y: my + normal.y * TRIANGLE_RADIUS * towardSelf,
            };
        }
        case 'pos_avoid_overlap': {
            // Push away from all allies within 5m. The anchor is current pos plus a
            // sum of repulsion vectors.
            let push = { x: 0, y: 0 };
            for (const ally of allies) {
                const d = distance(self.pos, ally.pos);
                if (d < 5 && d > 0) {
                    const away = direction(ally.pos, self.pos);
                    push = {
                        x: push.x + away.x * (5 - d),
                        y: push.y + away.y * (5 - d),
                    };
                }
            }
            return { x: self.pos.x + push.x, y: self.pos.y + push.y };
        }
        case 'pos_safe_line': {
            // Find the ally closest to the front line (closest to nearest enemy).
            const enemies = state.entities.filter((e) => e.side !== self.side && e.hp > 0);
            if (enemies.length === 0 || allies.length === 0)
                return self.pos;
            const nearestEnemy = enemies.length === 1
                ? enemies[0]
                : enemies.reduce((best, e) => distance(self.pos, e.pos) < distance(self.pos, best.pos) ? e : best);
            const frontline = allies.reduce((best, a) => distance(a.pos, nearestEnemy.pos) < distance(best.pos, nearestEnemy.pos) ? a : best);
            const away = direction(nearestEnemy.pos, frontline.pos);
            return {
                x: frontline.pos.x + away.x * SAFE_LINE_BACK,
                y: frontline.pos.y + away.y * SAFE_LINE_BACK,
            };
        }
    }
}
function pickAlly(allies, score) {
    if (allies.length === 0)
        return null;
    let best = allies[0];
    for (let i = 1; i < allies.length; i++) {
        if (score(allies[i]) > score(best))
            best = allies[i];
    }
    return best;
}
function flankPoint(self, target, sign) {
    if (!target)
        return self.pos;
    const dir = direction(target.pos, self.pos);
    // 90° rotation: (x, y) -> (-y, x) for left, (y, -x) for right.
    const perp = sign > 0 ? { x: -dir.y, y: dir.x } : { x: dir.y, y: -dir.x };
    return {
        x: target.pos.x + scale(perp, FLANK_RADIUS).x,
        y: target.pos.y + scale(perp, FLANK_RADIUS).y,
    };
}
//# sourceMappingURL=position.js.map